/**
 * Real subscription service implementation that connects to our backend APIs.
 * This replaces the mock implementation with actual calls to Stripe endpoints.
 */

import type {
  ISubscriptionService,
  UserEntitlements,
  UserSubscription,
  PurchaseResult,
  SubscriptionsResponse,
} from './types';
import { isSignedInAs } from '../../lib/auth/signedInAs';
import { authenticatedApi } from '../../lib/authenticated-api';
import {
  EMAIL_NOT_VERIFIED,
  EMAIL_NOT_VERIFIED_MESSAGES,
  isEmailNotVerifiedError,
} from '../../lib/emailNotVerified';
import {
  CHECKOUT_CONFLICT_MESSAGES,
  HEAPSTERLIVE_SUBSCRIPTION_EXISTS,
  readCheckoutConflict,
} from '../../lib/checkoutConflict';

/**
 * Production subscription service implementation.
 * Connects to backend subscription and entitlement APIs.
 */
export class ApiSubscriptionService implements ISubscriptionService {
  private currentUserId: string | null = null;

  constructor(userId?: string) {
    this.currentUserId = userId ?? null;
  }

  /**
   * Set the current user ID.
   */
  setUserId(userId: string): void {
    this.currentUserId = userId;
  }

  /**
   * Check if user has a specific entitlement.
   */
  async hasEntitlement(entitlementId: string): Promise<boolean> {
    if (!this.currentUserId) {
      return false;
    }

    try {
      const response = await authenticatedApi.request<{
        hasEntitlement: boolean;
      }>(`/v1/users/${this.currentUserId}/entitlements/${entitlementId}`);

      if (!response.success) {
        console.error('Failed to check entitlement:', response.error);
        return false;
      }

      return response.data?.hasEntitlement || false;
    } catch (error) {
      console.error('Error checking entitlement:', error);
      return false;
    }
  }

  /**
   * Get all active entitlements for the user.
   * Transforms API response (ISO strings) to frontend format (Date objects).
   *
   * Rejects when the read fails (#2218). It used to resolve `{}`, which reads
   * as "no entitlements", and Subscribe is offered from that answer.
   */
  async getActiveEntitlements(): Promise<UserEntitlements> {
    if (!this.currentUserId) {
      return {};
    }

    try {
      // API returns entitlements with ISO 8601 string dates. As of the
      // source-collapse (#1646) per-provider detail lives in a provider-tagged
      // `subscriptions[]` array (the old singular `subscription`/`external`
      // fields are gone).
      interface ApiSubscriptionRecord {
        provider: string;
        isActive: boolean;
        expiresAt: string | null;
        willRenew?: boolean;
        purchaseDate?: string;
        productIdentifier?: string;
        providerSubscriptionId?: string;
      }
      interface ApiEntitlement {
        isActive: boolean;
        expiresAt: string | null;
        sources: string[];
        name?: string;
        description?: string;
        subscriptions?: ApiSubscriptionRecord[];
        manual?: {
          expiresAt: string | null;
          grantedBy: string;
          grantedAt: string;
          reason: string;
        };
      }

      const response = await authenticatedApi.request<
        Record<string, ApiEntitlement>
      >(`/v1/users/${this.currentUserId}/entitlements`);

      if (!response.success) {
        throw new Error(response.error || 'Failed to load entitlements');
      }

      const apiData = response.data || {};

      // Transform API response to frontend format
      const entitlements: UserEntitlements = {};
      for (const [id, apiEntitlement] of Object.entries(apiData)) {
        // Surface the Stripe-billing record specifically (active preferred,
        // else any present stripe record). Drives the flat willRenew/product
        // fields and the Stripe-only "Manage" gate on SubscriptionManagePage —
        // `sources` is provider-agnostic so it can't distinguish Stripe alone.
        const stripeRecords = (apiEntitlement.subscriptions ?? []).filter(
          s => s.provider === 'stripe'
        );
        const stripeSub =
          stripeRecords.find(s => s.isActive) ?? stripeRecords[0];

        entitlements[id] = {
          id,
          isActive: apiEntitlement.isActive,
          name: apiEntitlement.name,
          description: apiEntitlement.description,
          sources: apiEntitlement.sources,
          // Active-only: a cancelled/expired Stripe record must NOT light up the
          // Stripe billing card (the entitlement may still be active via a manual
          // or supapass grant). A Stripe sub cancelled-but-not-yet-expired stays
          // isActive:true, so users can still reach the portal to resume it.
          hasStripeSubscription: stripeRecords.some(s => s.isActive),
          // Exact: no case folding, and no "anything but Stripe" (#2463).
          hasSupapassSubscription: (apiEntitlement.subscriptions ?? []).some(
            s => s.provider === 'supapass' && s.isActive === true
          ),
          willRenew: stripeSub?.willRenew ?? false,
          // Null stays null. It used to become a date a hundred years out,
          // which /profile then showed as an expiry (#2463).
          periodEndDate: apiEntitlement.expiresAt
            ? new Date(apiEntitlement.expiresAt)
            : null,
          // The Stripe record's own date. `expiresAt` above is merged across
          // every active source, so it is not when Stripe next bills (#2209).
          stripePeriodEndDate: stripeSub?.expiresAt
            ? new Date(stripeSub.expiresAt)
            : undefined,
          purchaseDate: stripeSub?.purchaseDate
            ? new Date(stripeSub.purchaseDate)
            : undefined,
          productIdentifier: stripeSub?.productIdentifier,
        };
      }

      return entitlements;
    } catch (error) {
      console.error('Error getting entitlements:', error);
      throw error;
    }
  }

  /**
   * Get all subscriptions for the user (plural endpoint).
   * Returns an array of subscriptions for future multi-subscription support.
   *
   * Rejects when the read fails (#2218). It used to resolve an empty list,
   * which reads as "no subscription".
   */
  async getSubscriptions(): Promise<SubscriptionsResponse> {
    if (!this.currentUserId) {
      return { subscriptions: [] };
    }

    try {
      const response = await authenticatedApi.request<SubscriptionsResponse>(
        `/v1/users/${this.currentUserId}/subscriptions`
      );

      if (!response.success) {
        throw new Error(response.error || 'Failed to load subscriptions');
      }

      return response.data || { subscriptions: [] };
    } catch (error) {
      console.error('Error getting subscriptions:', error);
      throw error;
    }
  }

  /**
   * Get the full subscription data for the user.
   *
   * NOTE: This method now uses the plural subscriptions endpoint internally
   * and returns the first subscription. Entitlements must be fetched separately
   * using getActiveEntitlements().
   *
   * Null means the user has no subscription. A failed read rejects, through
   * `getSubscriptions` (#2218).
   */
  async getSubscription(): Promise<UserSubscription | null> {
    if (!this.currentUserId) {
      return null;
    }

    // Use plural endpoint and extract first subscription
    const subscriptionsResponse = await this.getSubscriptions();

    if (
      !subscriptionsResponse.subscriptions ||
      subscriptionsResponse.subscriptions.length === 0
    ) {
      // No subscriptions found - expected for free users
      return null;
    }

    // Get first subscription (currently single-subscription model)
    const firstSub = subscriptionsResponse.subscriptions[0];

    // Map SubscriptionData to UserSubscription format
    // Note: entitlements are not included in plural endpoint - must be fetched separately
    return {
      uid: firstSub.subscriptionId,
      entitlements: undefined, // Must call getActiveEntitlements() separately
      updatedAt: undefined, // Plural endpoint doesn't include timestamps
      createdAt: undefined,
    };
  }

  /**
   * Get entitlement expiration date.
   */
  async getEntitlementExpiration(entitlementId: string): Promise<Date | null> {
    // Fetch entitlements separately - they may not be included in subscription response
    const entitlements = await this.getActiveEntitlements();

    const entitlement = entitlements[entitlementId];
    if (!entitlement?.isActive) {
      return null;
    }

    return entitlement.periodEndDate;
  }

  /**
   * Get available products and pricing information.
   * This is a public endpoint that does not require authentication.
   */
  async getProducts(): Promise<{
    products: Array<{
      productId: string;
      name: string;
      description: string;
      entitlement: string;
      prices: Array<{
        currency: string;
        amount: number;
        priceId: string;
        interval: string;
      }>;
    }>;
  }> {
    const response = await authenticatedApi.request<{
      products: Array<{
        productId: string;
        name: string;
        description: string;
        entitlement: string;
        prices: Array<{
          currency: string;
          amount: number;
          priceId: string;
          interval: string;
        }>;
      }>;
    }>('/v1/products');

    if (!response.success || !response.data) {
      console.error('Failed to get products:', response.error);
      throw new Error(response.error || 'Failed to load pricing information');
    }

    return response.data;
  }

  /**
   * Start Stripe Checkout flow for purchasing a subscription.
   * @param priceId - The Stripe Price ID to purchase
   */
  async purchaseProduct(priceId: string): Promise<PurchaseResult> {
    if (!this.currentUserId) {
      return {
        success: false,
        error: 'User not authenticated',
      };
    }

    // Who asked. `currentUserId` belongs to a shared instance and follows
    // whoever is signed in, so it can change while the request is in flight.
    const requestedBy = this.currentUserId;

    try {
      // Detect if running on localhost for E2E testing
      const isLocalhost =
        window.location.origin.includes('localhost') ||
        window.location.origin.includes('127.0.0.1');

      // Build request body
      const requestBody: { priceId: string; success_url?: string } = {
        priceId,
      };

      // Include custom success_url for local E2E testing
      // This allows Stripe to redirect back to localhost after payment
      // Note: Using 'sid' instead of 'session_id' to avoid URL truncation issue
      if (isLocalhost) {
        requestBody.success_url = `${window.location.origin}/subscription/success?sid={CHECKOUT_SESSION_ID}`;
      }

      // Create Stripe checkout session
      const response = await authenticatedApi.request<{
        sessionId: string;
        sessionUrl: string;
      }>('/v1/checkout/session', {
        method: 'POST',
        body: JSON.stringify(requestBody),
      });

      if (!response.success || !response.data) {
        return {
          success: false,
          error: response.error || 'Failed to create checkout session',
        };
      }

      // The session belongs to whoever asked for it. If another account has
      // signed in on this page since (a sign-in in another tab reaches this
      // one), they are not sent to it (#2218).
      if (!isSignedInAs(requestedBy)) {
        return {
          success: false,
          error: 'Failed to create checkout session',
        };
      }

      // Redirect to Stripe Checkout
      window.location.href = response.data.sessionUrl;

      // This won't actually execute due to redirect, but satisfies the interface
      return {
        success: true,
        entitlements: ['premium'], // Will be determined by Stripe
      };
    } catch (error) {
      console.error('Purchase error:', error);
      // Mapped HERE, while the `ApiHttpError` and its `code` are still intact:
      // the result below carries only strings. The backend refuses checkout
      // to an unverified address (#2037) even when this app's own check
      // passed on a token minted before an un-verify.
      if (isEmailNotVerifiedError(error)) {
        return {
          success: false,
          error: EMAIL_NOT_VERIFIED_MESSAGES.purchase,
          errorCode: EMAIL_NOT_VERIFIED,
        };
      }
      // The same, for the refusal of someone who already has a HeapsterLive
      // subscription (#2218). Its category is the shared `CONFLICT`, so it is
      // read from `context.code`.
      const conflict = readCheckoutConflict(error);
      if (conflict) {
        return {
          success: false,
          error: CHECKOUT_CONFLICT_MESSAGES[conflict.reason],
          errorCode: HEAPSTERLIVE_SUBSCRIPTION_EXISTS,
          conflictReason: conflict.reason,
        };
      }
      const code = (error as { code?: unknown } | null)?.code;
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Purchase failed',
        ...(typeof code === 'string' && { errorCode: code }),
      };
    }
  }

  /**
   * Restore previous purchases.
   * For web subscriptions, this refreshes subscription status from backend.
   *
   * Rejects if the sync did not complete. That relies on `authenticatedApi`
   * throwing on every non-2xx, which is also why the backend must never report
   * a failed sync as `200 {success:false}` — this method would have resolved
   * successfully (#1731).
   */
  async restorePurchases(): Promise<void> {
    if (!this.currentUserId) {
      throw new Error('User not authenticated');
    }

    try {
      // For web subscriptions, we sync with backend to get latest subscription status
      await authenticatedApi.request<{ success: boolean }>(
        `/v1/users/${this.currentUserId}/subscription/sync`,
        {
          method: 'POST',
          body: JSON.stringify({}),
        }
      );
    } catch (error) {
      console.error('Restore purchases error:', error);
      throw error;
    }
  }
}

/**
 * Create a real subscription service instance.
 */
export function createApiSubscriptionService(
  userId?: string
): ISubscriptionService {
  return new ApiSubscriptionService(userId);
}
